import { useResyncEstimateMutation, useResyncHistoricalMutation } from '@/redux/api/api.caller';
import commonSlice from '@/redux/features/common.slice';
import { handleToastMutation } from '@/helpers/toast';
import type { IHistoricalSyncEstimate, ResyncHistoricalDays } from '@/types/historical-sync';
import { PATH } from '@/constants/path';
import { Banner, BlockStack, Button, List, Modal, Select, Text } from '@shopify/polaris';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

interface Props {
  open: boolean;
  onClose: () => void;
  onSynced?: (estimatedNewOrders: number) => void;
}

const OPTIONS: { label: string; value: ResyncHistoricalDays }[] = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 60 days', value: 60 },
];

const formatDate = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

const formatQuota = (v: number | 'UNLIMITED') => (v === 'UNLIMITED' ? '∞' : String(v));

const SyncPastOrdersModal = ({ open, onClose, onSynced }: Props) => {
  const dispatch = useDispatch();
  const [rangeDays, setRangeDays] = useState<ResyncHistoricalDays>(7);
  const [estimate, setEstimate] = useState<IHistoricalSyncEstimate | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);

  const [runEstimate, { isLoading: isEstimating }] = useResyncEstimateMutation();
  const [runResync, { isLoading: isSyncing }] = useResyncHistoricalMutation();

  useEffect(() => {
    if (!open) {
      setEstimate(null);
      setEstimateError(null);
      return;
    }
    runEstimate({ rangeDays })
      .unwrap()
      .then((data) => {
        setEstimate(data);
        setEstimateError(null);
      })
      .catch(() => {
        setEstimate(null);
        setEstimateError('Unable to estimate orders. Please try again.');
      });
  }, [open, rangeDays, runEstimate]);

  const quotaAfter = useMemo(() => {
    if (!estimate) return null;
    if (estimate.shopQuota === 'UNLIMITED') return 'UNLIMITED' as const;
    return Math.min(Number(estimate.shopQuota), estimate.currentUsage + estimate.estimatedNewOrders);
  }, [estimate]);

  const handleSubmit = async () => {
    try {
      const response = await runResync({ rangeDays }).unwrap();
      dispatch(commonSlice.actions.handleToast(handleToastMutation({ data: response } as any)));
      onSynced?.(response.estimatedNewOrders);
      onClose();
    } catch (err: any) {
      dispatch(
        commonSlice.actions.handleToast({
          isOpen: true,
          content: err?.data?.message || 'Sync failed. Please try again.',
          error: true,
        }),
      );
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Sync past orders"
      primaryAction={{
        content: 'Sync orders',
        onAction: handleSubmit,
        loading: isSyncing,
        disabled: !estimate || isEstimating,
      }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <Text as="p">
            Sync past orders from up to 60 days ago. Synced orders will{' '}
            <Text as="span" fontWeight="bold">
              count toward your current quota cycle
            </Text>{' '}
            and{' '}
            <Text as="span" fontWeight="bold">
              cannot be undone
            </Text>
            . Orders already synced won&apos;t be counted twice.
          </Text>

          <Select
            label="Sync orders from the last"
            options={OPTIONS.map((o) => ({ label: o.label, value: String(o.value) }))}
            value={String(rangeDays)}
            onChange={(v) => setRangeDays(Number(v) as ResyncHistoricalDays)}
            disabled={isEstimating || isSyncing}
          />

          {estimateError && <Banner tone="critical">{estimateError}</Banner>}

          {estimate && !estimateError && (
            <List type="bullet">
              <List.Item>
                Date range:{' '}
                <Text as="span" fontWeight="bold">
                  {formatDate(estimate.fromDate)} – {formatDate(estimate.toDate)}
                </Text>
              </List.Item>
              <List.Item>
                Estimated orders:{' '}
                <Text as="span" fontWeight="bold">
                  ~ {estimate.estimatedNewOrders} orders
                </Text>{' '}
                <Text as="span" tone="subdued">
                  (Already-synced orders are excluded)
                </Text>
              </List.Item>
              <List.Item>
                Current quota:{' '}
                <Text as="span" fontWeight="bold">
                  {estimate.currentUsage} / {formatQuota(estimate.shopQuota)}
                </Text>{' '}
                → After syncing:{' '}
                <Text as="span" fontWeight="bold">
                  ~ {quotaAfter === 'UNLIMITED' ? formatQuota('UNLIMITED') : quotaAfter} /{' '}
                  {formatQuota(estimate.shopQuota)}
                </Text>
              </List.Item>
            </List>
          )}

          {estimate?.willExceedQuota && estimate.shopQuota !== 'UNLIMITED' && (
            <Banner tone="warning">
              ~{estimate.overflowBy} orders will be over quota and paused from tracking.{' '}
              <Button variant="plain" url={PATH.PLAN}>Upgrade plan</Button>
            </Banner>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
};

export default SyncPastOrdersModal;
